'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { LinkProps } from '@mui/material/Link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import AppleIcon from '@mui/icons-material/Apple';
import GitHubIcon from '@mui/icons-material/GitHub';
import PasswordIcon from '@mui/icons-material/Password';
import Button, { ButtonProps } from '@mui/material/Button';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { FormControlLabelProps } from '@mui/material/FormControlLabel';
import { alpha, SxProps, useTheme, type Theme } from '@mui/material/styles';

import { AppRoutes } from 'config';

import { RouterContext, BrandingContext } from '../shared/context';
import {
  useLocaleText,
  type LocaleText,
} from '../AppProvider/LocalizationProvider';

import LineIcon from './icons/Line';
import OktaIcon from './icons/Okta';
import SlackIcon from './icons/Slack';
import Auth0Icon from './icons/Auth0';
import GoogleIcon from './icons/Google';
import TikTokIcon from './icons/TikTok';
import TwitchIcon from './icons/Twitch';
import GitLabIcon from './icons/GitLab';
import TwitterIcon from './icons/Twitter';
import SpotifyIcon from './icons/Spotify';
import DiscordIcon from './icons/Discord';
import CognitoIcon from './icons/Cognito';
import FacebookIcon from './icons/Facebook';
import LinkedInIcon from './icons/LinkedIn';
import KeycloakIcon from './icons/Keycloak';
import InstagramIcon from './icons/Instagram';
import FusionAuthIcon from './icons/FusionAuth';
import MicrosoftEntraIdIcon from './icons/MicrosoftEntra';

const mergeSlotSx = (
  defaultSx: SxProps<Theme>,
  slotProps?: { sx?: SxProps<Theme> }
) => {
  if (Array.isArray(slotProps?.sx)) {
    return [defaultSx, ...slotProps.sx];
  }

  if (slotProps?.sx) {
    return [defaultSx, slotProps?.sx];
  }

  return [defaultSx];
};

type TextFieldSlotProps = TextFieldProps['slotProps'];

const getRemovedAsteriskProps = (
  slotProps?: TextFieldSlotProps
): TextFieldSlotProps => ({
  ...slotProps,
  inputLabel: {
    ...slotProps?.inputLabel,
    required: false,
  },
});

const getCommonTextFieldProps = (
  theme: Theme,
  baseProps: TextFieldProps = {}
): TextFieldProps => ({
  required: true,
  fullWidth: true,
  ...baseProps,
  slotProps: {
    ...baseProps.slotProps,
    htmlInput: {
      ...baseProps.slotProps?.htmlInput,
      sx: mergeSlotSx(
        {
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
        },
        typeof baseProps.slotProps?.htmlInput === 'function'
          ? {}
          : baseProps.slotProps?.htmlInput
      ),
    },
    inputLabel: {
      ...baseProps.slotProps?.inputLabel,
      sx: mergeSlotSx(
        {
          fontSize: theme.typography.pxToRem(14),
          lineHeight: theme.typography.pxToRem(12),
        },
        typeof baseProps.slotProps?.inputLabel === 'function'
          ? {}
          : baseProps.slotProps?.inputLabel
      ),
    },
  },
});

type SupportedOAuthProvider =
  | 'line'
  | 'okta'
  | 'apple'
  | 'slack'
  | 'auth0'
  | 'github'
  | 'google'
  | 'gitlab'
  | 'tiktok'
  | 'twitch'
  | 'twitter'
  | 'spotify'
  | 'discord'
  | 'cognito'
  | 'facebook'
  | 'linkedin'
  | 'keycloak'
  | 'instagram'
  | 'fusionauth'
  | 'microsoft-entra-id';

export type SupportedAuthProvider =
  string | 'passkey' | 'nodemailer' | 'credentials' | SupportedOAuthProvider;

const IconProviderMap = new Map<SupportedAuthProvider, React.ReactNode>([
  ['github', <GitHubIcon key="github" />],
  ['credentials', <PasswordIcon key="credentials" />],
  ['google', <GoogleIcon key="google" />],
  ['facebook', <FacebookIcon key="facebook" />],
  ['passkey', <FingerprintIcon key="passkey" />],
  ['twitter', <TwitterIcon key="twitter" />],
  ['apple', <AppleIcon key="apple" />],
  ['instagram', <InstagramIcon key="instagram" />],
  ['tiktok', <TikTokIcon key="tiktok" />],
  ['linkedin', <LinkedInIcon key="linkedin" />],
  ['slack', <SlackIcon key="slack" />],
  ['spotify', <SpotifyIcon key="spotify" />],
  ['twitch', <TwitchIcon key="twitch" />],
  ['discord', <DiscordIcon key="discord" />],
  ['line', <LineIcon key="line" />],
  ['auth0', <Auth0Icon key="auth0" />],
  ['microsoft-entra-id', <MicrosoftEntraIdIcon key="microsoft-entra-id" />],
  ['cognito', <CognitoIcon key="cognito" />],
  ['gitlab', <GitLabIcon key="gitlab" />],
  ['keycloak', <KeycloakIcon key="keycloak" />],
  ['okta', <OktaIcon key="okta" />],
  ['fusionauth', <FusionAuthIcon key="fusionauth" />],
]);

interface SignInPageLocaleText {
  or: string;
  to: string;
  with: string;
  email: string;
  passkey: string;
  password: string;
  signInSubtitle: string;
  signInRememberMe: string;
  providerSignInTitle: (provider: string) => string;
  signInTitle: ((brandingTitle?: string) => string) | string;
}

export interface OAuthProviderRenderContext {
  /**
   * Id of the native form rendered by SignInPage for this custom provider.
   */
  formId: string;
  /**
   * Whether this provider is currently selected and loading.
   */
  loading: boolean;
  /**
   * Whether this provider is currently selected.
   */
  selected: boolean;
  /**
   * Callback URL resolved by SignInPage.
   */
  callbackUrl: string;
  /**
   * Provider being rendered.
   */
  provider: AuthProvider;
  /**
   * Visual defaults for the built-in OAuth button.
   */
  defaultButtonProps: ButtonProps;
  /**
   * Form props of the native form rendered by SignInPage for this custom provider.
   */
  formProps: React.FormHTMLAttributes<HTMLFormElement>;
  /**
   * Executes provider sign-in and keeps SignInPage status in sync.
   */
  submit: (formData?: FormData) => Promise<AuthResponse | void | undefined>;
  /**
   * Renders SignInPage native submit button with slots.submitButton fallback.
   */
  renderSubmitButton: (
    label?: React.ReactNode,
    overrides?: ButtonProps
  ) => React.ReactNode;
}

export interface AuthProvider {
  /**
   * The name of the authentication provider.
   * @default ''
   * @example 'Google'
   * @example 'GitHub'
   */
  name: string;
  /**
   * The unique identifier of the authentication provider.
   * @default undefined
   * @example 'google'
   * @example 'github'
   */
  id: SupportedAuthProvider;
  /**
   * Optional custom renderer for OAuth-like providers.
   * If omitted, SignInPage renders the default OAuth button.
   * @example ({ submit, defaultButtonProps }) => (
   *   <Button {...defaultButtonProps} onClick={() => void submit()}>
   *     Continue with SSO
   *   </Button>
   * )
   */
  render?: (context: OAuthProviderRenderContext) => React.ReactNode;
}

export interface AuthResponse {
  /**
   * The type of error if the sign-in failed.
   * @default ''
   */
  type?: string;
  /**
   * The error message if the sign-in failed.
   * @default ''
   */
  error?: string;
  /**
   * The success notification if the sign-in was successful.
   * @default ''
   * Only used for magic link sign-in.
   * @example 'Check your email for a magic link.'
   */
  success?: string;
}

export interface SignInPageSlots {
  /**
   * A component to override the default title section
   * @default Typography
   */
  title?: React.ElementType;
  /**
   * A component to override the default subtitle section
   * @default Typography
   */
  subtitle?: React.ElementType;
  /**
   * A custom checkbox placed in the credentials form
   * @default FormControlLabel
   */
  rememberMe?: React.ElementType;
  /**
   * The custom sign up link component used in the credentials form.
   * @default Link
   */
  signUpLink?: React.JSXElementConstructor<LinkProps>;
  /**
   * The custom submit button component used in the credentials form.
   * @default Button
   */
  submitButton?: React.JSXElementConstructor<ButtonProps>;
  /**
   * The custom email field component used in the credentials form.
   * @default TextField
   */
  emailField?: React.JSXElementConstructor<TextFieldProps>;
  /**
   * The custom password field component used in the credentials form.
   * @default TextField
   */
  passwordField?: React.JSXElementConstructor<TextFieldProps>;
  /**
   * The custom forgot password link component used in the credentials form.
   * @default Link
   */
  forgotPasswordLink?: React.JSXElementConstructor<LinkProps>;
}

export interface SignInPageProps {
  /**
   * The prop used to customize the styles on the `SignInPage` container
   */
  sx?: SxProps;
  /**
   * The components used for each slot inside.
   * @default {}
   * @example { forgotPasswordLink: <Link href="/forgot-password">Forgot password?</Link> }
   * @example { signUpLink: <Link href="/sign-up">Sign up</Link> }
   */
  slots?: SignInPageSlots;
  /**
   * The list of authentication providers to display.
   * @default []
   */
  providers?: AuthProvider[];
  /**
   * The labels for the account component.
   */
  localeText?: Partial<SignInPageLocaleText>;
  /**
   * Callback fired when a user signs in.
   * @param {AuthProvider} provider The authentication provider.
   * @param {FormData} formData The form data if the provider id is 'credentials'.\
   * @param {string} callbackUrl The URL to redirect to after signing in.
   * @returns {void|Promise<AuthResponse>}
   * @default undefined
   */
  signIn?: (
    provider: AuthProvider,
    formData?: any,
    callbackUrl?: string
  ) => Promise<AuthResponse> | void | undefined;
  /**
   * The props used for each slot inside.
   * @default {}
   * @example { emailField: { autoFocus: false } }
   * @example { passwordField: { variant: 'outlined' } }
   * @example { emailField: { autoFocus: false }, passwordField: { variant: 'outlined' } }
   */
  slotProps?: {
    signUpLink?: LinkProps;
    oAuthButton?: ButtonProps;
    submitButton?: ButtonProps;
    emailField?: TextFieldProps;
    passwordField?: TextFieldProps;
    forgotPasswordLink?: LinkProps;
    rememberMe?: Partial<FormControlLabelProps>;
    form?: Partial<React.FormHTMLAttributes<HTMLFormElement>>;
  };
}

const defaultLocaleText: Pick<LocaleText, keyof SignInPageLocaleText> = {
  or: 'or',
  to: 'to',
  with: 'with',
  email: 'Email',
  passkey: 'Passkey',
  password: 'Password',
  signInRememberMe: 'Remember me',
  signInSubtitle: 'Please sign in to continue',
  providerSignInTitle: (provider: string) => `Sign in with ${provider}`,
  signInTitle: (brandingTitle?: string) =>
    brandingTitle ? `Sign in to ${brandingTitle}` : 'Sign in',
};

/**
 *
 * Demos:
 *
 * - [Sign-in Page](https://mui.com/toolpad/core/react-sign-in-page/)
 *
 * API:
 *
 * - [SignInPage API](https://mui.com/toolpad/core/api/sign-in-page)
 */
function SignInPage(props: SignInPageProps) {
  const {
    sx,
    slots,
    signIn,
    providers,
    slotProps,
    localeText: propsLocaleText,
  } = props;
  const theme = useTheme();
  const branding = React.useContext(BrandingContext);
  const router = React.useContext(RouterContext);
  const globalLocaleText = useLocaleText();
  const localeText = {
    ...defaultLocaleText,
    ...globalLocaleText,
    ...propsLocaleText,
  };

  const [{ error, loading, success, selectedProviderId }, setFormStatus] =
    React.useState<{
      error?: string;
      loading: boolean;
      success?: string;
      selectedProviderId?: SupportedAuthProvider;
    }>({
      error: '',
      success: '',
      loading: false,
      selectedProviderId: undefined,
    });

  const callbackUrl = router?.searchParams.get('callbackUrl') ?? AppRoutes.Home;
  const singleProvider = React.useMemo(
    () => providers?.length === 1,
    [providers]
  );

  const isOauthProvider = React.useCallback(
    (provider?: SupportedAuthProvider) =>
      provider &&
      provider !== 'credentials' &&
      provider !== 'nodemailer' &&
      provider !== 'passkey',
    []
  );
  const { oauthProviders, nonOauthProviders, customOauthProviders } =
    React.useMemo(() => {
      return Object.values(providers ?? {}).reduce<{
        oauthProviders: AuthProvider[];
        nonOauthProviders: AuthProvider[];
        customOauthProviders: AuthProvider[];
      }>(
        (acc, provider) => {
          if (isOauthProvider(provider.id)) {
            if (provider.render) {
              acc.customOauthProviders.push(provider);
            } else {
              acc.oauthProviders.push(provider);
            }
          } else {
            acc.nonOauthProviders.push(provider);
          }

          return acc;
        },
        {
          oauthProviders: [],
          nonOauthProviders: [],
          customOauthProviders: [],
        }
      );
    }, [isOauthProvider, providers]);

  const hasOauthProvider = React.useMemo(
    () => oauthProviders.length > 0,
    [oauthProviders]
  );

  const isPasskeyProvider = React.useCallback(
    (provider?: SupportedAuthProvider) => provider && provider === 'passkey',
    []
  );

  const isEmailProvider = React.useCallback(
    (provider?: SupportedAuthProvider) => provider && provider === 'nodemailer',
    []
  );

  const isCredentialsProvider = React.useCallback(
    (provider?: SupportedAuthProvider) =>
      provider && provider === 'credentials',
    []
  );

  const submitOAuthProvider = React.useCallback(
    async (provider: AuthProvider, formData?: FormData) => {
      setFormStatus({
        error: '',
        loading: true,
        selectedProviderId: provider.id,
      });
      const oauthResponse = await signIn?.(provider, formData, callbackUrl);
      setFormStatus((prev) => ({
        ...prev,
        error: oauthResponse?.error,
        loading: oauthResponse?.error ? false : prev.loading,
      }));
      return oauthResponse;
    },
    [callbackUrl, signIn]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        ...sx,
      }}
    >
      <Container maxWidth="xs" component="main">
        <Stack
          sx={{
            p: 4,
            gap: 1,
            display: 'flex',
            borderRadius: 1,
            border: '1px solid',
            alignItems: 'center',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[4],
            borderColor: alpha(theme.palette.grey[400], 0.4),
          }}
        >
          {branding?.logo}

          {slots?.title ? (
            <slots.title />
          ) : (
            <Typography
              variant="h5"
              component="h1"
              color="textPrimary"
              sx={{
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {typeof localeText.signInTitle === 'string'
                ? localeText.signInTitle
                : localeText.signInTitle(branding?.title)}
            </Typography>
          )}
          {slots?.subtitle ? (
            <slots.subtitle />
          ) : (
            <Typography
              gutterBottom
              variant="body2"
              color="textSecondary"
              sx={{
                textAlign: 'center',
              }}
            >
              {localeText?.signInSubtitle}
            </Typography>
          )}
          <Box sx={{ width: '100%' }}>
            <Stack spacing={1}>
              {error && isOauthProvider(selectedProviderId) ? (
                <Alert severity="error">{error}</Alert>
              ) : null}
              {oauthProviders.map((provider: AuthProvider) => {
                const isSelectedProvider = selectedProviderId === provider.id;
                const isSelectedProviderLoading = loading && isSelectedProvider;
                const defaultOauthButtonProps: ButtonProps = {
                  size: 'large',
                  type: 'submit',
                  fullWidth: true,
                  name: 'provider',
                  color: 'inherit',
                  value: provider.id,
                  variant: 'outlined',
                  disableElevation: true,
                  loading: isSelectedProviderLoading,
                  startIcon: IconProviderMap.get(provider.id),
                  sx: {
                    textTransform: 'capitalize',
                  },
                  ...slotProps?.oAuthButton,
                };

                return (
                  <form
                    key={provider.id}
                    onSubmit={async (event) => {
                      event.preventDefault();
                      await submitOAuthProvider(provider);
                    }}
                    {...slotProps?.form}
                  >
                    <Button {...defaultOauthButtonProps}>
                      <span>
                        {localeText.providerSignInTitle(provider.name)}
                      </span>
                    </Button>
                  </form>
                );
              })}
            </Stack>

            {nonOauthProviders.map((provider: AuthProvider, index: number) => {
              return (
                <React.Fragment key={provider.id}>
                  {isPasskeyProvider(provider.id) ? (
                    <React.Fragment>
                      {hasOauthProvider || index > 0 ? (
                        <Divider sx={{ mt: 2, mx: 0, mb: 1 }}>
                          {localeText.or}
                        </Divider>
                      ) : null}
                      {error && selectedProviderId === 'passkey' ? (
                        <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                          {error}
                        </Alert>
                      ) : null}
                      <Box
                        component="form"
                        onSubmit={async (event) => {
                          setFormStatus({
                            error: '',
                            loading: true,
                            selectedProviderId: provider.id,
                          });
                          event.preventDefault();
                          const formData = new FormData(event.currentTarget);
                          const passkeyResponse = await signIn?.(
                            provider,
                            formData,
                            callbackUrl
                          );
                          setFormStatus((prev) => ({
                            ...prev,
                            loading: false,
                            error: passkeyResponse?.error,
                          }));
                        }}
                        {...slotProps?.form}
                      >
                        {slots?.emailField ? (
                          <slots.emailField {...slotProps?.emailField} />
                        ) : (
                          <TextField
                            {...getCommonTextFieldProps(theme, {
                              name: 'email',
                              type: 'email',
                              sx: { mt: 1 },
                              id: 'email-passkey',
                              label: localeText.email,
                              autoFocus: singleProvider,
                              placeholder: 'your@email.com',
                              autoComplete: 'email-webauthn',
                              ...slotProps?.emailField,
                            })}
                          />
                        )}
                        {slots?.submitButton ? (
                          <slots.submitButton {...slotProps?.submitButton} />
                        ) : (
                          <Button
                            fullWidth
                            size="large"
                            type="submit"
                            color="inherit"
                            disableElevation
                            variant="outlined"
                            startIcon={IconProviderMap.get(provider.id)}
                            loading={
                              loading && selectedProviderId === provider.id
                            }
                            sx={{
                              mt: 3,
                              mb: 2,
                              textTransform: 'capitalize',
                            }}
                            {...slotProps?.submitButton}
                          >
                            {localeText.providerSignInTitle(
                              provider.name || localeText.passkey
                            )}
                          </Button>
                        )}
                      </Box>
                    </React.Fragment>
                  ) : null}
                  {isEmailProvider(provider.id) ? (
                    <React.Fragment>
                      {hasOauthProvider || index > 0 ? (
                        <Divider sx={{ mt: 2, mx: 0, mb: 1 }}>
                          {localeText.or}
                        </Divider>
                      ) : null}
                      {error && selectedProviderId === 'nodemailer' ? (
                        <Alert sx={{ my: 1 }} severity="error">
                          {error}
                        </Alert>
                      ) : null}
                      {success && selectedProviderId === 'nodemailer' ? (
                        <Alert sx={{ my: 1 }} severity="success">
                          {success}
                        </Alert>
                      ) : null}
                      <Box
                        component="form"
                        onSubmit={async (event) => {
                          event.preventDefault();
                          setFormStatus({
                            error: '',
                            loading: true,
                            selectedProviderId: provider.id,
                          });
                          const formData = new FormData(event.currentTarget);
                          const emailResponse = await signIn?.(
                            provider,
                            formData,
                            callbackUrl
                          );
                          setFormStatus((prev) => ({
                            ...prev,
                            loading: false,
                            error: emailResponse?.error,
                            success: emailResponse?.success,
                          }));
                        }}
                        {...slotProps?.form}
                      >
                        {slots?.emailField ? (
                          <slots.emailField {...slotProps?.emailField} />
                        ) : (
                          <TextField
                            {...getCommonTextFieldProps(theme, {
                              name: 'email',
                              type: 'email',
                              sx: { mt: 1 },
                              id: 'email-nodemailer',
                              label: localeText.email,
                              autoFocus: singleProvider,
                              placeholder: 'your@email.com',
                              autoComplete: 'email-nodemailer',
                              ...slotProps?.emailField,
                            })}
                          />
                        )}
                        {slots?.submitButton ? (
                          <slots.submitButton {...slotProps?.submitButton} />
                        ) : (
                          <Button
                            fullWidth
                            size="large"
                            type="submit"
                            color="inherit"
                            disableElevation
                            variant="outlined"
                            id="submit-nodemailer"
                            loading={
                              loading && selectedProviderId === provider.id
                            }
                            sx={{
                              mt: 3,
                              mb: 2,
                              textTransform: 'capitalize',
                            }}
                            {...slotProps?.submitButton}
                          >
                            {localeText.providerSignInTitle(
                              (
                                provider.name || localeText.email
                              ).toLocaleLowerCase()
                            )}
                          </Button>
                        )}
                      </Box>
                    </React.Fragment>
                  ) : null}
                  {isCredentialsProvider(provider.id) ? (
                    <React.Fragment>
                      {hasOauthProvider || index > 0 ? (
                        <Divider sx={{ mt: 2, mx: 0, mb: 1 }}>
                          {localeText.or}
                        </Divider>
                      ) : null}
                      {error && selectedProviderId === 'credentials' ? (
                        <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                          {error}
                        </Alert>
                      ) : null}
                      <Box
                        component="form"
                        onSubmit={async (event) => {
                          setFormStatus({
                            error: '',
                            loading: true,
                            selectedProviderId: provider.id,
                          });
                          event.preventDefault();
                          const formData = new FormData(event.currentTarget);
                          const credentialsResponse = await signIn?.(
                            provider,
                            formData,
                            callbackUrl
                          );
                          setFormStatus((prev) => ({
                            ...prev,
                            loading: false,
                            error: credentialsResponse?.error,
                          }));
                        }}
                        {...slotProps?.form}
                      >
                        <Stack
                          spacing={2}
                          direction="column"
                          sx={{
                            marginTop: 1,
                          }}
                        >
                          {slots?.emailField ? (
                            <slots.emailField {...slotProps?.emailField} />
                          ) : (
                            <TextField
                              {...getCommonTextFieldProps(theme, {
                                id: 'email',
                                name: 'email',
                                type: 'email',
                                autoComplete: 'email',
                                label: localeText.email,
                                autoFocus: singleProvider,
                                placeholder: 'your@email.com',
                                ...slotProps?.emailField,
                                slotProps: getRemovedAsteriskProps(
                                  slotProps?.emailField?.slotProps
                                ),
                              })}
                            />
                          )}
                          {slots?.passwordField ? (
                            <slots.passwordField
                              {...slotProps?.passwordField}
                            />
                          ) : (
                            <TextField
                              {...getCommonTextFieldProps(theme, {
                                id: 'password',
                                name: 'password',
                                type: 'password',
                                placeholder: '*****',
                                label: localeText.password,
                                autoComplete: 'current-password',
                                ...slotProps?.passwordField,
                                slotProps: getRemovedAsteriskProps(
                                  slotProps?.passwordField?.slotProps
                                ),
                              })}
                            />
                          )}
                        </Stack>
                        {slots?.forgotPasswordLink || slots?.rememberMe ? (
                          <Stack
                            spacing={1}
                            direction="row"
                            sx={{
                              mt: 2,
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            {slots?.rememberMe ? (
                              <slots.rememberMe {...slotProps?.rememberMe} />
                            ) : null}
                            {slots?.forgotPasswordLink ? (
                              <slots.forgotPasswordLink
                                {...slotProps?.forgotPasswordLink}
                              />
                            ) : null}
                          </Stack>
                        ) : null}
                        {slots?.submitButton ? (
                          <slots.submitButton {...slotProps?.submitButton} />
                        ) : (
                          <Button
                            fullWidth
                            size="large"
                            type="submit"
                            color="inherit"
                            disableElevation
                            variant="outlined"
                            loading={
                              loading && selectedProviderId === provider.id
                            }
                            sx={{
                              mt: 3,
                              mb: 2,
                              textTransform: 'capitalize',
                            }}
                            {...slotProps?.submitButton}
                          >
                            {localeText.providerSignInTitle(
                              (
                                provider.name || localeText.password
                              ).toLocaleLowerCase()
                            )}
                          </Button>
                        )}

                        {slots?.signUpLink ? (
                          <Box
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            <slots.signUpLink {...slotProps?.signUpLink} />
                          </Box>
                        ) : null}
                      </Box>
                    </React.Fragment>
                  ) : null}
                </React.Fragment>
              );
            })}

            {customOauthProviders.map(
              (provider: AuthProvider, index: number) => {
                const isSelectedProvider = selectedProviderId === provider.id;
                const isSelectedProviderLoading = loading && isSelectedProvider;
                const formId = `oauth-provider-${provider.id}`;
                const handleCustomProviderSubmit = async (
                  event: React.FormEvent<HTMLFormElement>
                ) => {
                  event.preventDefault();
                  const formData = new FormData(event.currentTarget);
                  await submitOAuthProvider(provider, formData);
                };
                const formProps: React.FormHTMLAttributes<HTMLFormElement> = {
                  ...slotProps?.form,
                  id: formId,
                  onSubmit: handleCustomProviderSubmit,
                };
                const defaultOauthButtonProps: ButtonProps = {
                  size: 'large',
                  type: 'submit',
                  fullWidth: true,
                  name: 'provider',
                  color: 'inherit',
                  value: provider.id,
                  variant: 'outlined',
                  disableElevation: true,
                  loading: isSelectedProviderLoading,
                  startIcon: IconProviderMap.get(provider.id),
                  sx: {
                    mt: 3,
                    mb: 2,
                    textTransform: 'capitalize',
                  },
                  ...slotProps?.submitButton,
                };

                return (
                  <React.Fragment key={provider.id}>
                    {(oauthProviders.length > 0 ||
                      nonOauthProviders.length > 0 ||
                      index > 0) && (
                      <Divider sx={{ mt: 2, mx: 0, mb: 1 }}>
                        {localeText.or}
                      </Divider>
                    )}
                    <Box component="form" {...formProps}>
                      {provider.render?.({
                        formId,
                        provider,
                        formProps,
                        callbackUrl,
                        selected: isSelectedProvider,
                        loading: isSelectedProviderLoading,
                        defaultButtonProps: defaultOauthButtonProps,
                        submit: (formData) =>
                          submitOAuthProvider(provider, formData),
                        renderSubmitButton: (label, overrides) => {
                          const mergedButtonProps: ButtonProps = {
                            ...defaultOauthButtonProps,
                            ...overrides,
                          };

                          if (slots?.submitButton) {
                            return (
                              <slots.submitButton {...mergedButtonProps} />
                            );
                          }

                          return (
                            <Button {...mergedButtonProps}>
                              {label ??
                                localeText.providerSignInTitle(
                                  (provider.name || '').toLocaleLowerCase()
                                )}
                            </Button>
                          );
                        },
                      })}
                    </Box>
                  </React.Fragment>
                );
              }
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

SignInPage.propTypes /* remove-proptypes */ = {
  /**
   * Callback fired when a user signs in.
   * @param {AuthProvider} provider The authentication provider.
   * @param {FormData} formData The form data if the provider id is 'credentials'.\
   * @param {string} callbackUrl The URL to redirect to after signing in.
   * @returns {void|Promise<AuthResponse>}
   * @default undefined
   */
  signIn: PropTypes.func,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * The labels for the account component.
   */
  localeText: PropTypes.object,
  /**
   * The list of authentication providers to display.
   * @default []
   */
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      render: PropTypes.func,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  /**
   * The prop used to customize the styles on the `SignInPage` container
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * The props used for each slot inside.
   * @default {}
   * @example { emailField: { autoFocus: false } }
   * @example { passwordField: { variant: 'outlined' } }
   * @example { emailField: { autoFocus: false }, passwordField: { variant: 'outlined' } }
   */
  slotProps: PropTypes.shape({
    form: PropTypes.object,
    emailField: PropTypes.object,
    rememberMe: PropTypes.object,
    signUpLink: PropTypes.object,
    oAuthButton: PropTypes.object,
    submitButton: PropTypes.object,
    passwordField: PropTypes.object,
    forgotPasswordLink: PropTypes.object,
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   * @example { forgotPasswordLink: <Link href="/forgot-password">Forgot password?</Link> }
   * @example { signUpLink: <Link href="/sign-up">Sign up</Link> }
   */
  slots: PropTypes.shape({
    title: PropTypes.elementType,
    subtitle: PropTypes.elementType,
    emailField: PropTypes.elementType,
    rememberMe: PropTypes.elementType,
    signUpLink: PropTypes.elementType,
    submitButton: PropTypes.elementType,
    passwordField: PropTypes.elementType,
    forgotPasswordLink: PropTypes.elementType,
  }),
} as any;

export { SignInPage };
